import type {
  OktaAuth,
  TokenManagerInterface,
  Tokens,
} from "@okta/okta-auth-js";

/** Okta IDX stores transaction state here; clear to avoid "session expired" when starting OTP */
export const OKTA_TRANSACTION_STORAGE_KEY = "okta-transaction-storage";

/** @okta/okta-auth-js HTTP/metadata cache (see CACHE_STORAGE_NAME in the SDK) */
export const OKTA_CACHE_STORAGE_KEY = "okta-cache-storage";

/** Clears IDX transaction storage and @okta/okta-auth-js HTTP cache keys from session/local storage. */
export function clearOktaAuthStorage(): void {
  try {
    sessionStorage.removeItem(OKTA_TRANSACTION_STORAGE_KEY);
    localStorage.removeItem(OKTA_TRANSACTION_STORAGE_KEY);
    sessionStorage.removeItem(OKTA_CACHE_STORAGE_KEY);
    localStorage.removeItem(OKTA_CACHE_STORAGE_KEY);
  } catch {
    console.error("Failed to clear Okta auth storage");
  }
}

const oauthBaseFromIssuer = (issuer: string): string => {
  const trimmed = issuer.replace(/\/$/, "");
  return trimmed.includes("/oauth2") ? trimmed : `${trimmed}/oauth2`;
};

/**
 * RP-initiated OIDC logout (GET `/oauth2/.../v1/logout?client_id=...`) with cookies sent to Okta.
 * Best-effort: may fail silently on CORS/network; does not redirect the page.
 * Call before `idx.authenticate` to drop an existing Okta SSO session when starting a fresh IDX login.
 */
export async function clearOktaSessionWithLogoutEndpoint(
  oktaAuth: OktaAuth
): Promise<boolean> {
  const issuer = oktaAuth.options.issuer;
  const clientId = oktaAuth.options.clientId;
  if (!issuer || !clientId || typeof fetch === "undefined") {
    console.warn(
      "clearOktaSessionWithLogoutEndpoint: Missing required parameters"
    );
    return false;
  }
  const url = `${oauthBaseFromIssuer(issuer)}/v1/logout?${new URLSearchParams({
    client_id: clientId,
  }).toString()}`;
  try {
    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      mode: "cors",
      cache: "no-store",
    });
    if (!response.ok) {
      console.warn(
        `clearOktaSessionWithLogoutEndpoint: HTTP ${response.status}`
      );
      return false;
    }
    return true;
  } catch (error) {
    console.warn("clearOktaSessionWithLogoutEndpoint: Failed", error);
    return false;
  }
}

export interface SetOktaTokensParams {
  accessToken: string;
  idToken?: string;
  refreshToken?: string;
}

/**
 * Builds a Tokens-shaped object from raw token strings and passes it to Okta tokenManager.
 * Uses minimal token shape; Okta SDK accepts this at runtime despite stricter typings.
 */
export function setOktaTokens(
  tokenManager: TokenManagerInterface,
  params: SetOktaTokensParams
): void {
  const tokens: Tokens = {
    accessToken: {
      accessToken: params.accessToken,
      tokenType: "Bearer",
    } as Tokens["accessToken"],
    idToken: params.idToken
      ? ({ idToken: params.idToken } as Tokens["idToken"])
      : undefined,
    refreshToken: params.refreshToken
      ? ({ refreshToken: params.refreshToken } as Tokens["refreshToken"])
      : undefined,
  };
  tokenManager.setTokens(tokens);
}
